/*
 * Copyright (c) 2025, Glasswing AI, Inc.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 * Glasswing AI, Inc. ("Confidential Information").
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Glasswing AI, Inc.
 */
import { useParams } from 'react-router';
import { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Stack } from 'react-bootstrap';
import { JsonEditor } from 'json-edit-react'
import * as dayjs from 'dayjs'
import { getProbe, getProbeChildren } from '../data'
import { StatusContext, StatusContextType } from './StatusContext';
import ProbeDetail from './ProbeDetail';
import { Offcanvas, Badge, Button, ButtonToolbar } from 'react-bootstrap';
import { useTheme } from 'styled-components';

function ProbeEditor() {
    const [probeData, setProbeData] = useState<any>({ loading: true, data: null });
    const [showDetail, setShowDetail] = useState(false);
    let { probeId } = useParams();
    const statusContext = useContext(StatusContext);
    const theme : any = useTheme();

    useEffect(() => {
        async function fetchProbeData() {
            try {
                const pd = await getProbe(Number(probeId));
                const children = await getProbeChildren(Number(probeId));
                pd.children = children;
                setProbeData({ loading: false, data: pd });
            } catch (error : any) {
                console.error('Error fetching probe data:', error);
                setProbeData({ loading: true, data: null });
                statusContext.postError(`Failed to load probe data for ID ${probeId}: ${error.message}`);
            }   
        }
        fetchProbeData();
    }, [ probeId ]);

    if (probeData.loading) {
        return (
            <Container fluid>
                <Row className="mt-4">
                    <Col>
                        <h2>Loading probe data for {probeId}.</h2>
                    </Col>
                </Row>
            </Container>
        );
    }
    const { data } : { data: any }  = probeData;
    let pfbadge;
    if (data.pending) {
        pfbadge = null; // No badge for pending probes
    } else if (data.passed) {
        pfbadge = <Badge pill bg="success">PASS</Badge>
    } else {
        pfbadge = <Badge pill bg="danger">FAIL</Badge>
    }
    let ptdisplay = `${data.probe_type.toUpperCase()}`;
    if (data.detail?.desc) {
        ptdisplay += ` - ${data.detail?.desc}`;
    }

    return (
        <div style={{ backgroundColor: theme.colors.bg, color: theme.colors.ctext }} className="container-fluid">
        <Container className="ms-4" fluid>
            <Row className="mt-2">
                <Col className="col-6">
                    <h2>{ptdisplay} {pfbadge}</h2>
                </Col>
                <Col className="col-6">
                    <ButtonToolbar className="float-end me-5">
                        <>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowDetail(true);
                            }}
                            title="Raw Details"
                        >Raw</Button>
                        </>
                    </ButtonToolbar>
                </Col>
            </Row>
            <Row>
                <ProbeDetail probe={data} />
            </Row>
        </Container>
        <Offcanvas
            style={{ width: '60vw' }}
            show={showDetail}
            onHide={() => { setShowDetail(false); }}
            onClose={() => { setShowDetail(false); }}
            placement="end"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Raw Probe Details</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <JsonEditor
                    maxWidth="100%"
                    data={data.detail}
                    onUpdate={({ newData }) => {
                        console.log('Updated data:', newData);
                    }}
                />
            </Offcanvas.Body>
        </Offcanvas>
        </div>
    );
}

export default ProbeEditor;